/** @jsx jsx */
import { Form, Formik } from 'formik'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useInterval } from 'react-use'
import { jsx } from 'theme-ui'
import * as Yup from 'yup'
import {
  Box,
  Button,
  Col,
  Collapse,
  FormattedCurrency,
  FormGroupInput,
  FormGroupLabel,
  Heading,
  LoadingPlaceholder,
  Row,
  Txt,
} from '../../components'
import { AsyncActionButton } from '../../components/AsyncActionButton'
import { lastCardContent } from '../../components/Card'
import { ConnectedFormGroup, ConnectedFormGroupDivider } from '../../components/ConnectedFormField'
import { FormattedUserName } from '../../components/FormattedUserName'
import { FormErrorMessage, GeneralFormErrorMessage } from '../../components/FormErrorMessage'
import { IModalProps, Modal } from '../../components/Modal'
import { UserProfilePicture } from '../../components/UserProfilePicture'
import {
  ClientGameHandController,
  IClientUserData,
} from '../../controllers/ClientGameHandController'
import { maybePlural } from '../../utils/formatting'
import { captureExceptionWithLabel } from '../../utils/sentry'
import { IPositionProps } from './positionProps'
import { IGameActions } from './types'
import { UpdateGameRulesForm } from './UpdateGameRulesForm'
import { GiveUserAdditionalChipsForm } from './GiveUserAdditionalChipsForm'
import { Ellipsis } from '../../components/Ellipsis'

export const CurrentUserOptionsModal: React.FC<
  Pick<IModalProps, 'isOpen' | 'onClose'> & {
    controller: ClientGameHandController
    actions: IGameActions
  }
> = ({ controller, onClose, isOpen, actions }) => {
  const [visibleForm, setVisibleForm] = useState<'give-chips' | 'show-rules' | null>(null)
  const [hasUpdatedRules, setHasUpdatedRules] = useState(false)
  const { currentUserData } = controller

  useEffect(() => {
    if (!isOpen) {
      setVisibleForm(null)
    }
  }, [isOpen])
  return (
    <Modal
      {...{ onClose, isOpen }}
      label={`Game Hand Details for ${currentUserData.email}`}
      header={
        <Heading as="h3">
          <Ellipsis sx={{ color: 'lightGreen' }}>
            <FormattedUserName user={currentUserData} />
          </Ellipsis>
        </Heading>
      }
    >
      <Txt>
        {(() => {
          switch (currentUserData.tableState) {
            case 'AWAY':
              return 'Away (set to auto-check/auto-fold)'
            case 'OUT':
              return 'Out of the game'
            case 'PLAYING':
              return 'Currently playing'
          }
        })()}
      </Txt>
      <Txt>
        <Txt as="span" sx={{ fontWeight: 'bold' }}>
          Chips in current pot:{' '}
        </Txt>
        <FormattedCurrency amount={currentUserData.amountInvested} />
      </Txt>
      <Txt>
        <Txt as="span" sx={{ fontWeight: 'bold' }}>
          Chips left:{' '}
        </Txt>
        <FormattedCurrency amount={currentUserData.amountLeftToBet} /> (
        {Math.floor((currentUserData.amountLeftToBet / controller.totalChips) * 100)}% of table)
      </Txt>
      <Txt>
        <Txt as="span" sx={{ fontWeight: 'bold' }}>
          Total chips given:{' '}
        </Txt>
        <FormattedCurrency amount={currentUserData.totalAmountGiven} />
      </Txt>
      {!!currentUserData.pendingAmountGiven && (
        <Txt sx={{ color: 'lightGreen' }}>
          <Txt as="span" sx={{ fontWeight: 'bold' }}>
            Pending chips given next hand:{' '}
          </Txt>
          <FormattedCurrency amount={currentUserData.pendingAmountGiven} />
        </Txt>
      )}
      {!!currentUserData.votesToSetUserToAway.length && (
        <Txt>
          {currentUserData.votesToSetUserToAway.length}{' '}
          {maybePlural('vote', currentUserData.votesToSetUserToAway)} to set you to away (
          {Math.ceil(controller.usersInGameTablePosition.length * 0.5) -
            currentUserData.votesToSetUserToAway.length +
            1}{' '}
          more needed)
        </Txt>
      )}
      {currentUserData.role === 'ADMIN' && (
        <Row>
          <Col sx={{ mt: [3, null, null, 5] }} xs={12} md={6}>
            <Button
              sx={{ color: 'darkBlue', background: 'transparent' }}
              block
              onClick={() => {
                setVisibleForm(visibleForm === 'show-rules' ? null : 'show-rules')
              }}
            >
              Update Game Rules
            </Button>
          </Col>
          <Col sx={{ mt: [3, null, null, 5] }} xs={12} md={6}>
            <Button
              sx={{ color: 'darkBlue', background: 'transparent' }}
              block
              onClick={() => setVisibleForm(visibleForm === 'give-chips' ? null : 'give-chips')}
            >
              Give additional chips
            </Button>
          </Col>
        </Row>
      )}
      <Collapse isOpened={hasUpdatedRules}>
        <Box sx={{ pt: 5, color: 'lightGreen', fontWeight: 'bold' }}>
          Rules will update next hand
        </Box>
      </Collapse>
      <Collapse isOpened={visibleForm === 'give-chips'}>
        <Box sx={{ pt: 5 }}>
          <GiveUserAdditionalChipsForm
            onSuccess={() => {
              setVisibleForm(null)
            }}
            {...{ userData: currentUserData, actions }}
          />
        </Box>
      </Collapse>
      <Collapse isOpened={visibleForm === 'show-rules'}>
        <Box sx={{ pt: 5 }}>
          <UpdateGameRulesForm
            onSuccess={() => {
              setHasUpdatedRules(true)
              setVisibleForm(null)
            }}
            gameHand={controller.gameHand}
          />
        </Box>
      </Collapse>
    </Modal>
  )
}
